import * as React from 'react';
import { Link } from 'react-router-dom';
import ReactTable from 'react-table';
import { Column } from 'react-table';
import { Control, LocalForm, Errors } from 'react-redux-form';
import { QuoteSearchViewModel } from '@models/Quote';
import { getDefaultErrorMessages } from '@common/validators';

import { AddressViewModel } from '@models/common.models';
import { CheckboxInline } from '@common/components';
import { dateParser } from '@common/dateParser';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { Role, Roles } from '@models/Roles';
import { TooltipComponent } from '@common/components/tooltip/tooltip.component';

export interface QuoteSearchComponentProperties {
  readonly message: string;
  readonly query: string | null;
  readonly quotesLoading: boolean;
  readonly quotesPageNumber: number;
  readonly quotesPageSize: number;
  readonly quoteSearchList: QuoteSearchViewModel[];
  readonly quotesPageCount: number;
  readonly quotesOrderBy: string;
  readonly filterByConverted: boolean;
  readonly getQuotes: (
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string,
    filterByConverted: boolean
  ) => void;
  readonly reset: () => void;
}

interface FormModel {
  readonly query: string;
  readonly converted: boolean;
}

// Component definition
export class QuoteSearchComponent extends React.Component<QuoteSearchComponentProperties> {
  private isInitialized: boolean = false;

  componentWillUnmount() {
    this.props.reset();
  }

  renderError() {
    if (!this.props.message) {
      return <div />;
    }
    return <div className="error">{this.props.message.toString()}</div>;
  }

  updateFilter() {
    return;
  }

  render() {
    const columns: Column[] = [
      {
        accessor: (q: QuoteSearchViewModel) =>
          q ? (
            <Link to={getRoute(RouteDefinitions.quote.Details, { id: +q.id! })} target="_blank">
              {' '}
              {q.quoteNumber}{' '}
            </Link>
          ) : (
            'N/A'
          ),
        maxWidth: 100,
        id: 'quote',
        Header: 'Quote #',
        sortable: true
      },
      {
        accessor: 'contactName',
        Header: `Caller's Name`,
        maxWidth: 200,
        sortable: true
      },
      {
        accessor: 'updatedDate',
        maxWidth: 150,
        Cell: (row: { value: string }) => (
          <span>{row && row.value !== null ? dateParser.parse(row.value).format('l') : '--'}</span>
        ),
        Header: 'Last Modified Date',
        sortable: false
      },
      {
        accessor: 'contactEmail',
        maxWidth: 150,
        Header: 'Email',
        sortable: true
      },
      {
        accessor: 'address',
        Header: 'Address',
        Cell: (row: { value: AddressViewModel }) => (
          <span>
            {row.value
              ? `${row.value.line1} ${row.value.line2} ${row.value.city} ${row.value.state.abbreviation} ${
                  row.value.zip
                }`
              : ''}
          </span>
        ),
        sortable: false
      },
      {
        accessor: 'converted',
        Header: 'Status',
        Cell: (row: { value: string | null }) => <span>{row && row.value ? 'CONVERTED' : 'OPEN'}</span>,
        maxWidth: 100,
        sortable: true
      }
    ];

    return (
      <div className="pb-10">
        <LocalForm
          className="form-horizontal"
          hideNativeErrors={true}
          onSubmit={(props: FormModel) => this.handleSearch(props.query, props.converted)}
        >
          <div className="row">
            <div className="col-md-12">
              <h2>Quotes</h2>
            </div>
            <div className="form-control col-md-12">
              <label htmlFor=".query">Search</label>
              <TooltipComponent text={'Search by Address, Contact Name, Contact Email, or Quote ID'} side={'top'} />
              <Control.text model=".query" />
              <Errors
                className="error"
                model=".query"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
              {this.props.quotesPageCount * 10 > 10000 && (
                <span> Your search is too broad. Narrow the search with more specific terms. </span>
              )}
            </div>
          </div>
          <div className="row center-xs">
            <div className="form-control mr-10">
              <CheckboxInline className="end-xs">
                <Control.checkbox model=".converted" />
                <label htmlFor="local.converted">Show Converted Only</label>
              </CheckboxInline>
            </div>
            <button className="btn btn-primary" disabled={this.props.quotesLoading} type="submit">
              Search
            </button>
          </div>
        </LocalForm>
        <ReactTable
          onPageChange={() => {
            Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
              element.scrollTop = 0;
            });
          }}
          className="-striped -highlight"
          columns={columns}
          data={this.props.quoteSearchList}
          defaultPageSize={10}
          loading={this.props.quotesLoading}
          manual={true}
          onFetchData={this.handleFetchData}
          pages={this.props.quotesPageCount}
        />
        {Role.CheckRoles([Roles.Admin, ...Role.GetAllIBS(), ...Role.GetAllAccounting()]) && (
          <Link className="btn btn-primary" to={RouteDefinitions.quote.Create}>
            Create New
          </Link>
        )}
      </div>
    );
  }

  private handleFetchData = (state: {
    page: number;
    pageSize: number;
    sorted: Array<{ id: string; desc: string }>;
  }): void => {
    const orderBy = state.sorted[0] ? state.sorted[0].id : 'id';
    const orderDirection = state.sorted[0] ? (state.sorted[0].desc ? 'desc' : 'asc') : 'asc';

    if (this.isInitialized) {
      this.props.getQuotes(
        this.props.query,
        state.pageSize,
        state.page + 1,
        orderBy,
        orderDirection,
        this.props.filterByConverted
      );
    }

    this.isInitialized = true;
  };

  private handleSearch = (query: string, filterByConverted: boolean): void => {
    this.isInitialized = true;

    this.props.getQuotes(query, this.props.quotesPageSize, 1, 'updated', '', filterByConverted);
  };
}



// WEBPACK FOOTER //
// ./src/quote/_components/quote-search/quote-search.component.tsx